import { useEffect, useState } from "react";
import { inventoryApi, type ItemBatch } from "../../../api/inventory";
import type { BasketBatch } from "../page";

interface BatchSelectModalProps {
  itemId: number;
  itemName: string;
  unit: string;
  warehouseId: number;
  allowFractional: boolean;
  // Batches already on the basket line — pre-selected with their quantities
  initialSelected: BasketBatch[];
  onClose: () => void;
  // The full batch selection for the line (empty when everything was deselected)
  onConfirm: (batches: BasketBatch[]) => void;
}

const formatDate = (value: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "-"
    : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const isExpired = (batch: ItemBatch) => {
  if (!batch.expiryDate) return false;
  const expiry = new Date(batch.expiryDate);
  return !Number.isNaN(expiry.getTime()) && expiry.getTime() < Date.now();
};

const roundQty = (value: number) => Math.round(value * 1000) / 1000;

export default function BatchSelectModal({
  itemId,
  itemName,
  unit,
  warehouseId,
  allowFractional,
  initialSelected,
  onClose,
  onConfirm,
}: BatchSelectModalProps) {
  const [batches, setBatches] = useState<ItemBatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Typed quantity per selected batch id (kept as text so "0." can be typed)
  const [selected, setSelected] = useState<Record<number, string>>(() =>
    Object.fromEntries(initialSelected.map((b) => [b.batchId, String(b.qty)])),
  );

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    inventoryApi
      .getItemBatches({ itemId, warehouseId })
      .then((list) => {
        if (!cancelled) setBatches(list);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || "Failed to load batches.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, warehouseId, reloadKey]);

  const parseQty = (value: string) => {
    const parsed = allowFractional ? parseFloat(value) : parseInt(value, 10);
    return Number.isFinite(parsed) ? roundQty(parsed) : NaN;
  };

  const getRowError = (batch: ItemBatch): string | null => {
    const raw = selected[batch.id];
    if (raw === undefined) return null;
    const qty = parseQty(raw);
    if (!Number.isFinite(qty) || qty <= 0) return "Enter a quantity";
    if (qty > batch.totalAvailableQuantity) {
      return `Only ${batch.totalAvailableQuantity} ${unit} available`;
    }
    return null;
  };

  const selectedIds = Object.keys(selected).map(Number);
  const hasErrors = batches.some((b) => getRowError(b) !== null);
  const totalQty = roundQty(
    Object.values(selected).reduce((sum, raw) => {
      const qty = parseQty(raw);
      return sum + (Number.isFinite(qty) && qty > 0 ? qty : 0);
    }, 0),
  );

  const toggleBatch = (batch: ItemBatch) => {
    setSelected((prev) => {
      if (prev[batch.id] !== undefined) {
        const rest = { ...prev };
        delete rest[batch.id];
        return rest;
      }
      return { ...prev, [batch.id]: "1" };
    });
  };

  const setBatchQty = (batch: ItemBatch, value: string) => {
    setSelected((prev) => ({ ...prev, [batch.id]: value }));
  };

  const stepBatchQty = (batch: ItemBatch, step: number) => {
    const current = parseQty(selected[batch.id] ?? "0");
    const next = roundQty(Math.min(batch.totalAvailableQuantity, Math.max(1, (Number.isFinite(current) ? current : 0) + step)));
    setBatchQty(batch, String(next));
  };

  const handleConfirm = () => {
    if (hasErrors) return;
    const result: BasketBatch[] = batches
      .filter((b) => selected[b.id] !== undefined)
      .map((b) => ({
        batchId: b.id,
        batchName: b.name,
        expiryDate: b.expiryDate,
        binInfo: b.binInfo,
        available: b.totalAvailableQuantity,
        qty: parseQty(selected[b.id]),
      }));
    onConfirm(result);
  };

  // Editing an existing line with everything deselected removes the line
  const canConfirm = !isLoading && !error && !hasErrors && (selectedIds.length > 0 || initialSelected.length > 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-stack-line text-teal-600 text-xl"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 whitespace-nowrap">Select Batches</h2>
              <p className="text-sm text-gray-600 truncate">{itemName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[200px]">
          {isLoading && (
            <div className="flex items-center justify-center py-10 text-gray-500 text-sm">
              <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
              Loading batches...
            </div>
          )}

          {!isLoading && error && (
            <div className="text-center py-10">
              <p className="text-sm text-red-600 mb-2">{error}</p>
              <button
                onClick={() => setReloadKey((k) => k + 1)}
                className="text-sm font-semibold text-teal-600 hover:text-teal-700 cursor-pointer"
              >
                Retry
              </button>
            </div>
          )}

          {!isLoading && !error && batches.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10">
              <i className="ri-inbox-line text-5xl text-gray-300 mb-2"></i>
              <p className="text-sm text-gray-600">No active batches available</p>
            </div>
          )}

          {!isLoading &&
            !error &&
            batches.map((batch) => {
              const expired = isExpired(batch);
              const outOfStock = batch.totalAvailableQuantity <= 0;
              const isSelected = selected[batch.id] !== undefined;
              // A batch already on the line stays editable so it can be deselected
              const disabled = (expired || outOfStock) && !isSelected;
              const rowError = getRowError(batch);
              return (
                <div
                  key={batch.id}
                  className={`p-3 rounded-lg border-2 transition-colors ${
                    disabled
                      ? "border-gray-200 bg-gray-50 opacity-60"
                      : isSelected
                        ? rowError
                          ? "border-red-400 bg-red-50"
                          : "border-teal-500 bg-teal-50"
                        : "border-gray-200 hover:border-teal-300"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={disabled}
                      onChange={() => toggleBatch(batch)}
                      className="mt-1 w-4 h-4 accent-teal-600 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <div
                      className={`flex-1 min-w-0 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                      onClick={() => !disabled && toggleBatch(batch)}
                    >
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-sm font-bold text-gray-900 truncate">{batch.name}</span>
                        {expired ? (
                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">
                            Expired
                          </span>
                        ) : outOfStock ? (
                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">
                            Out of Stock
                          </span>
                        ) : (
                          <span className="text-xs bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">
                            {batch.totalAvailableQuantity} {unit} available
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-600">
                        <span className="whitespace-nowrap">Mfg: {formatDate(batch.manufactureDate)}</span>
                        <span className={`whitespace-nowrap ${expired ? "text-red-600 font-semibold" : ""}`}>
                          Exp: {formatDate(batch.expiryDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {isSelected && (
                    <div className="mt-2 pl-7 flex items-center gap-2">
                      <span className="text-xs font-semibold text-gray-700">Qty</span>
                      <button
                        type="button"
                        onClick={() => stepBatchQty(batch, -1)}
                        className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded cursor-pointer"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <input
                        type="number"
                        autoFocus
                        onWheel={(e) => e.currentTarget.blur()}
                        value={selected[batch.id]}
                        onChange={(e) => setBatchQty(batch, e.target.value)}
                        onKeyDown={(e) => {
                          const blocked = allowFractional ? [",", "-", "+", "e", "E"] : [".", ",", "-", "+", "e", "E"];
                          if (blocked.includes(e.key)) e.preventDefault();
                          if (e.key === "Enter" && canConfirm) handleConfirm();
                        }}
                        step={allowFractional ? "any" : "1"}
                        min={allowFractional ? "0.001" : "1"}
                        max={batch.totalAvailableQuantity}
                        className={`w-20 px-2 py-1 text-sm text-center border rounded focus:outline-none ${
                          rowError ? "border-red-500 text-red-600" : "border-gray-300 focus:border-teal-500"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => stepBatchQty(batch, 1)}
                        className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded cursor-pointer"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                      <span className="text-xs text-gray-500">/ {batch.totalAvailableQuantity}</span>
                      {rowError && <span className="text-xs text-red-600 ml-auto">{rowError}</span>}
                    </div>
                  )}
                </div>
              );
            })}
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center gap-3">
          <div className="text-sm text-gray-700 whitespace-nowrap">
            Total: <span className="font-bold text-gray-900">{totalQty}</span> {unit}
            {selectedIds.length > 1 && <span className="text-gray-500"> · {selectedIds.length} batches</span>}
          </div>
          <div className="ml-auto flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {selectedIds.length === 0 && initialSelected.length > 0 ? "Remove Item" : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
